import React, { useState, useRef, useEffect } from 'react';
import { sendRequest } from '../../api';

const ConversationalTopicCreator = ({ onCreateTopic, onCancel }) => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'agent',
      text: "👋 Hi! I'm your personal intelligence agent. What would you like me to track on the internet for you?",
      timestamp: new Date()
    }
  ]);
  
  const [currentInput, setCurrentInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [conversationData, setConversationData] = useState({});
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const addMessage = (type, text) => {
    const newMessage = {
      id: messages.length + 1,
      type,
      text,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const addUserMessage = (text) => {
    addMessage('user', text);
    setCurrentInput('');
  };

  const addAgentMessage = (text) => {
    setIsTyping(true);
    setTimeout(() => {
      addMessage('agent', text);
      setIsTyping(false);
    }, 1000 + Math.random() * 1000); // Simulate thinking time
  };

  // Truly agentic conversation using AI
  const analyzeConversationAndRespond = async (userResponse) => {
    setIsAnalyzing(true);
    
    try {
      // Get conversation history
      const conversationHistory = messages.map(msg => ({
        role: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.text
      }));
      
      // Add the new user response
      conversationHistory.push({
        role: 'user',
        content: userResponse
      });

      const result = await sendRequest('/topics/ai/analyze-topic-conversation', 'POST', {
        conversation_history: conversationHistory,
        current_data: conversationData
      });
      
      if (result.should_create_topic) {
        // We have enough information - create the topic
        addAgentMessage(result.agent_response);
        
        // Wait a bit then create the topic
        setTimeout(() => {
          createTopicFromAI(result.topic_config);
        }, 2000);
      } else {
        // Ask the next intelligent question
        addAgentMessage(result.agent_response);
        
        // Update conversation data with extracted information
        if (result.extracted_data) {
          setConversationData(prev => ({ ...prev, ...result.extracted_data }));
        }
      }
    } catch (error) {
      console.error('Error analyzing conversation:', error);
      addAgentMessage("I'm having trouble understanding. Could you rephrase what you'd like me to track?");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const createTopicFromAI = (topicConfig) => {
    // The AI has determined the optimal topic configuration
    onCreateTopic(topicConfig);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentInput.trim() || isAnalyzing) return;
    
    addUserMessage(currentInput);
    
    // Let AI analyze and respond
    await analyzeConversationAndRespond(currentInput);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
    // Shift+Enter will naturally create a new line in textarea
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl h-[600px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center">
              <span className="text-white font-bold">AI</span>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Intelligence Agent Setup</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {isAnalyzing ? 'Analyzing your needs...' : 'Tell me what you want to track'}
              </p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[80%] ${message.type === 'user' ? 'order-1' : 'order-2'}`}>
                <div
                  className={`px-4 py-2 rounded-lg ${
                    message.type === 'user'
                      ? 'bg-purple-500 text-white rounded-br-sm'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-bl-sm'
                  }`}
                >
                  <p className="whitespace-pre-wrap">{message.text}</p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 px-1">
                  {formatTime(message.timestamp)}
                </p>
              </div>
              
              {message.type === 'agent' && (
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mr-2 order-1 flex-shrink-0">
                  <span className="text-white text-sm font-bold">AI</span>
                </div>
              )}
            </div>
          ))}
          
          {(isTyping || isAnalyzing) && (
            <div className="flex justify-start">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mr-2 flex-shrink-0">
                <span className="text-white text-sm font-bold">AI</span>
              </div>
              <div className="bg-gray-100 dark:bg-gray-700 rounded-lg rounded-bl-sm px-4 py-2">
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  {isAnalyzing && (
                    <span className="text-xs text-gray-500">Thinking...</span>
                  )}
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <textarea
              value={currentInput}
              onChange={(e) => setCurrentInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isAnalyzing ? "AI is thinking..." : "Type your answer... (Shift+Enter for new line)"}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none overflow-y-auto"
              disabled={isTyping || isAnalyzing}
              rows={1}
              style={{
                minHeight: '42px',
                maxHeight: '120px',
                lineHeight: '1.5'
              }}
              onInput={(e) => {
                // Auto-resize textarea based on content
                e.target.style.height = 'auto';
                e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
              }}
            />
            <button
              type="submit"
              disabled={!currentInput.trim() || isTyping || isAnalyzing}
              className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed self-end"
            >
              {isAnalyzing ? 'Analyzing' : 'Send'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ConversationalTopicCreator; 